/* MIS Industrial — global.js
   Progressive enhancement only: drawers, disclosure menus, quantity steppers,
   cart-count updates, status announcements. No frameworks. */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function announce(message) {
  const region = document.getElementById('A11yStatus');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => { region.textContent = message; }, 50);
}

/* ---------- Drawer controller (details-based, works without JS) ----------
   Markup: <details class="drawer" data-drawer><summary>…</summary>
           <div class="drawer__content"><div class="drawer__panel">…</div></div></details>
   Without JS the panel expands inline; JS upgrades it to a focus-trapped overlay. */
document.querySelectorAll('details[data-drawer]').forEach((details) => {
  const summary = details.querySelector('summary');
  const panel = details.querySelector('.drawer__panel');
  summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

  const trapFocus = (e) => {
    if (e.key === 'Escape') { details.open = false; return; }
    if (e.key !== 'Tab') return;
    const items = Array.from(details.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  details.addEventListener('toggle', () => {
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    if (details.open) {
      document.body.style.overflow = 'hidden';
      details.addEventListener('keydown', trapFocus);
      const first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    } else {
      document.body.style.overflow = '';
      details.removeEventListener('keydown', trapFocus);
      summary.focus();
    }
  });

  details.querySelectorAll('[data-drawer-close]').forEach((el) => {
    el.addEventListener('click', () => { details.open = false; });
  });
});

/* ---------- Desktop nav disclosure (details/summary) ---------- */
document.querySelectorAll('[data-nav-disclosure]').forEach((details) => {
  const summary = details.querySelector('summary');
  details.addEventListener('toggle', () => {
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  });
  details.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && details.open) { details.open = false; summary.focus(); }
  });
  details.addEventListener('focusout', (e) => {
    if (details.open && e.relatedTarget && !details.contains(e.relatedTarget)) details.open = false;
  });
});
document.addEventListener('click', (e) => {
  document.querySelectorAll('[data-nav-disclosure][open]').forEach((d) => {
    if (!d.contains(e.target)) d.open = false;
  });
});

/* ---------- Quantity stepper ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const direction = btn.dataset.step === 'down' ? -1 : 1;
        const step = Math.max(parseInt(this.input.step || '1', 10), 1);
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        let value = parseInt(this.input.value || String(min), 10) + (direction * step);
        value = Math.min(Math.max(value, min), max);
        this.input.value = value;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

/* ---------- Cart count sync ---------- */
document.addEventListener('cart:change', (e) => {
  const count = e.detail && typeof e.detail.item_count === 'number' ? e.detail.item_count : null;
  if (count === null) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });
  const link = document.querySelector('[data-cart-link]');
  if (link) {
    const template = link.getAttribute('data-cart-count-label') || '';
    link.setAttribute('aria-label', template.replace('[count]', count));
  }
});
